"use client";

import React from "react";
import { HeartPulse, BookOpen, Sprout, Baby, Syringe, Ambulance, GraduationCap, BookA, Wrench, Wheat, Leaf, Store, Clock, User, Phone } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
const categories = [{
  id: "roofing",
  label: "Roofing",
  iconName: "Wrench"
}, {
  id: "siding",
  label: "Siding",
  iconName: "Store"
}, {
  id: "decks",
  label: "Decks & Windows",
  iconName: "Leaf"
}];
const services = [{
  id: "r1",
  categoryId: "roofing",
  title: "Impact-Resistant Shingles",
  description: "Premium architectural shingles engineered to withstand severe weather, hail, and high-velocity winds.",
  hours: "Warranty: 50-Year Limited",
  contactName: "Master Shingle Applicator",
  contactRole: "Installation Lead",
  iconName: "Baby"
}, {
  id: "r2",
  categoryId: "roofing",
  title: "Metal Roofing Systems",
  description: "Ultra-durable, energy-efficient metal panels providing a sleek modern aesthetic and lifelong protection.",
  hours: "Warranty: Lifetime Transferable",
  contactName: "Metal Specialist",
  contactRole: "Precision Engineering",
  iconName: "Syringe"
}, {
  id: "r3",
  categoryId: "roofing",
  title: "Seamless Gutter Solutions",
  description: "Custom-extruded aluminum gutters designed to channel water safely away from your home's foundation.",
  hours: "Warranty: 20-Year Finish",
  contactName: "Gutter Tech Team",
  contactRole: "Drainage Management",
  iconName: "Ambulance"
}, {
  id: "s1",
  categoryId: "siding",
  title: "Fiber Cement Cladding",
  description: "High-performance James Hardie siding that resists fire, moisture damage, and extreme temperature shifts.",
  hours: "Warranty: 30-Year Product",
  contactName: "Certified Installer",
  contactRole: "Exterior Specialist",
  iconName: "GraduationCap"
}, {
  id: "s2",
  categoryId: "siding",
  title: "Insulated Vinyl Siding",
  description: "Premium vinyl panels with integrated foam backing for enhanced thermal resistance and impact protection.",
  hours: "Warranty: Limited Lifetime",
  contactName: "Siding Consultant",
  contactRole: "Energy Efficiency",
  iconName: "BookA"
}, {
  id: "s3",
  categoryId: "siding",
  title: "Fascia & Soffit Repair",
  description: "Essential structural maintenance to ensure proper attic ventilation and prevent roof-line deterioration.",
  hours: "Warranty: 10-Year Labor",
  contactName: "Structural Crew",
  contactRole: "Maintenance Lead",
  iconName: "Wrench"
}, {
  id: "d1",
  categoryId: "decks",
  title: "Custom Composite Decks",
  description: "Low-maintenance outdoor living spaces built with premium Trex materials for beauty that never fades.",
  hours: "Warranty: 25-Year Fade/Stain",
  contactName: "Deck Designer",
  contactRole: "Architectural Lead",
  iconName: "Wheat"
}, {
  id: "d2",
  categoryId: "decks",
  title: "Energy-Star Windows",
  description: "High-performance replacement windows with low-E glass to drastically reduce seasonal heating costs.",
  hours: "Warranty: Lifetime Glass",
  contactName: "Window Specialist",
  contactRole: "Thermal Performance",
  iconName: "Leaf"
}, {
  id: "d3",
  categoryId: "decks",
  title: "Pressure-Treated Wood",
  description: "Traditional wood decking solutions treated for maximum resistance against rot and insect damage.",
  hours: "Warranty: 15-Year Structural",
  contactName: "Lumber Expert",
  contactRole: "Timber Management",
  iconName: "Store"
}];
const renderIcon = (name: string, className: string = "w-6 h-6") => {
  switch (name) {
    case "HeartPulse":
      return <HeartPulse className={className} />;
    case "BookOpen":
      return <BookOpen className={className} />;
    case "Sprout":
      return <Sprout className={className} />;
    case "Baby":
      return <Baby className={className} />;
    case "Syringe":
      return <Syringe className={className} />;
    case "Ambulance":
      return <Ambulance className={className} />;
    case "GraduationCap":
      return <GraduationCap className={className} />;
    case "BookA":
      return <BookA className={className} />;
    case "Wrench":
      return <Wrench className={className} />;
    case "Wheat":
      return <Wheat className={className} />;
    case "Leaf":
      return <Leaf className={className} />;
    case "Store":
      return <Store className={className} />;
    default:
      return null;
  }
};
export default function CommunitySupportHub() {
  return <section data-section-id="230377" id="our-specialized-solutions" className="bg-background py-24 sm:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter text-foreground mb-6">
            Our Specialized Solutions
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Precision-engineered restoration and exterior services designed to protect your home and enhance architectural value.
          </p>
        </div>

        <Tabs defaultValue="roofing" className="w-full flex flex-col items-center">
          <TabsList className="grid w-full max-w-md grid-cols-3 mb-12 h-auto p-1.5 bg-muted/50 rounded-sm">
            {categories.map((category, index) => <TabsTrigger key={category.id} data-index={index} value={category.id} className="flex flex-col gap-2 py-3 px-4 rounded-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-300">
                <div className="text-primary">
                  {renderIcon(category.iconName, "w-5 h-5")}
                </div>
                <span className="font-default font-medium text-sm">
                  {category.label}
                </span>
              </TabsTrigger>)}
          </TabsList>

          {categories.map((category, catIndex) => <TabsContent key={category.id} data-index={catIndex} value={category.id} className="w-full mt-0 outline-none focus-visible:ring-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {services.filter(service => service.categoryId === category.id).map((service, srvIndex) => <Card key={service.id} data-index={srvIndex} className="group border border-border/50 shadow-sm hover:shadow-md transition-all duration-300 rounded-sm bg-card overflow-hidden flex flex-col h-full">
                      <CardHeader className="pb-4 pt-8 px-8">
                        <div className="w-14 h-14 rounded-sm bg-primary/10 flex items-center justify-center text-primary mb-6 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                          {renderIcon(service.iconName, "w-7 h-7")}
                        </div>
                        <CardTitle className="font-default text-xl text-card-foreground">
                          {service.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="px-8 flex-grow">
                        <CardDescription className="text-base text-muted-foreground font-sans leading-relaxed">
                          {service.description}
                        </CardDescription>
                      </CardContent>
                      <CardFooter className="px-8 pb-8 pt-4 flex flex-col items-start gap-4 border-t border-border/20 mt-auto">
                        <div className="flex items-center gap-3 text-sm text-foreground w-full">
                          <Clock className="w-4 h-4 text-primary shrink-0" />
                          <span className="font-mono font-medium">{service.hours}</span>
                        </div>
                        
                        
                      </CardFooter>
                    </Card>)}
              </div>
            </TabsContent>)}
        </Tabs>
      </div>
    </section>;
}